export class QueryKeys {
  static getFinancialDataKey = () => ["MARKET_DATA"];
  static getUsageDataKey = (address: string, startDate?: string, endDate?: string) => ["USAGE_DATA", address, startDate, endDate];
  static getUsageStatsDataKey = (address: string, startDate?: string, endDate?: string) => ["USAGE_STATS_DATA", address, startDate, endDate];
  static getDashboardDataKey = () => ["DASHBOARD_DATA"];
  static getTransactionsKey = (limit: number) => ["TRANSACTIONS", limit];
  static getAddressTransactionsKey = (address: string, skip: number, limit: number) => ["ADDRESS_TRANSACTIONS", address, skip, limit];
  static getAddressDeploymentsKey = (address: string, skip: number, limit: number, reverseSorting: boolean, filters: { [key: string]: string }) => [
    "ADDRESS_DEPLOYMENTS",
    address,
    skip,
    limit,
    reverseSorting,
    JSON.stringify(filters)
  ];
  static getValidatorsKey = () => ["VALIDATORS"];
  static getProposalsKey = () => ["PROPOSALS"];
  static getTemplateKey = (id: string) => ["SDL_TEMPLATES", id];
  static getManifestVersionKey = (sdl: string | undefined) => ["MANIFEST_VERSION", sdl];
  static getPublicTemplateKey = (id: string) => ["PUBLIC_TEMPLATE", id];
  static getUserTemplatesKey = (username: string) => ["USER_TEMPLATES", username];
  static getUserFavoriteTemplatesKey = (userId: string) => ["USER_FAVORITES_TEMPLATES", userId];
  static getUsernameAvailabilityKey = (username: string) => ["USERNAME_AVAILABILITY", username];
  // Deploy
  static getDeploymentListKey = (address: string, state?: string) => (state ? ["DEPLOYMENT_LIST", address, state] : ["DEPLOYMENT_LIST", address]);
  static getDeploymentDetailKey = (address: string, dseq?: string) => ["DEPLOYMENT_DETAIL", address, dseq].filter(Boolean);
  static getAllLeasesKey = (address: string, state?: string | readonly string[]) => {
    const stateKey = Array.isArray(state) ? state.join(",") : state;
    return stateKey ? ["ALL_LEASES", address, stateKey] : ["ALL_LEASES", address];
  };
  /** Extends the all-leases key so prefix-based invalidation after a deploy also refreshes the existence check. */
  static getLeaseExistenceKey = (address: string) => [...QueryKeys.getAllLeasesKey(address), "EXISTENCE"];
  static getLeasesKey = (address: string, dseq: string) => ["LEASE_LIST", address, dseq];
  static getDeploymentLeaseStatusesKey = (dseq: string) => ["LEASE_STATUS", dseq];
  static getLeaseStatusKey = (dseq: string, gseq: number, oseq: number) => [...QueryKeys.getDeploymentLeaseStatusesKey(dseq), gseq, oseq];
  static getAllProviderSearchesKey = () => ["PROVIDER_SEARCH"];
  static getProviderSearchKey = (params: object) => [...QueryKeys.getAllProviderSearchesKey(), params];
  static getProviderLocationsKey = () => ["PROVIDER_LOCATIONS"];
  static getProviderByAddressKey = (address: string) => ["PROVIDER_BY_ADDRESS", address];
  static getProviderRegionsKey = () => ["PROVIDER_REGIONS"];
  static getProviderDetailKey = (owner: string) => ["PROVIDERS", owner];
  static getDataNodeProvidersKey = () => ["DATA_NODE_PROVIDERS"];
  static getProviderStatusKey = (providerUri: string) => ["PROVIDER_STATUS", providerUri];
  static getFirstReachableProviderKey = (placementKey: string) => ["FIRST_REACHABLE_PROVIDER", placementKey];
  static getProviderActiveLeasesGraph = (providerAddress: string) => ["PROVIDER_ACTIVE_LEASES_GRAPH", providerAddress];
  static getBalancesKey = (address?: string) => (address ? ["BALANCES", address] : []);
  static getFeeAllowanceExistenceKey = (address: string) => ["FEE_ALLOWANCE_EXISTENCE", address];
  static getTemplatesKey = () => ["TEMPLATES"];
  static getProviderAttributesSchema = () => ["PROVIDER_ATTRIBUTES_SCHEMA"];
  static getGpuModelsKey = () => ["GPU_MODELS"];
  static getProviderGpusKey = (providerAddress: string) => ["PROVIDER_GPUS", providerAddress];
  static getLandTopologyKey = () => ["LAND_TOPOLOGY"];
  static getTrialProvidersKey = () => ["TRIAL_PROVIDERS"];
  static getTrialGpuAvailabilityKey = () => ["TRIAL_GPU_AVAILABILITY"];
  static getDeploymentSettingKey = (dseq: string) => ["DEPLOYMENT_SETTING", dseq];
  static getApiKeysKey = (userId: string) => ["API_KEYS", userId];

  // Remote deploy
  static getProviderTokenKey = () => ["TOKEN"];
  static getUserProfileKey = (accessToken?: string | null) => ["USER_PROFILE", accessToken];
  static getGroupsKey = (accessToken?: string | null) => ["GROUPS", accessToken];
  static getReposByGroupKey = (group?: string, accessToken?: string | null) => ["REPOS_BY_GROUP", group, accessToken];
  static getCommitsKey = (repo?: string, accessToken?: string | null) => ["COMMITS", repo, accessToken];
  static getCommitsByBranchKey = (repo?: string, branch?: string, accessToken?: string | null) => ["COMMITS_BY_BRANCH", repo, branch, accessToken];
  static getWorkspacesKey = (accessToken?: string | null) => ["WORKSPACES", accessToken];
  static getReposByWorkspaceKey = (workspace: string, accessToken: string | null) => ["REPOS", accessToken, workspace];
  static getReposKey = (installationsIds: number[], accessToken?: string | null) => ["REPOS", installationsIds, accessToken];
  static getInstallationsKey = (accessToken?: string | null) => ["INSTALLATIONS", accessToken];
  static getBranchesKey = (repo?: string, accessToken?: string | null) => ["BRANCHES", repo, accessToken];
  static getPackageJsonKey = (repo?: string, branch?: string, subFolder?: string) => ["PACKAGE_JSON", repo, branch, subFolder];
  static getSrcFoldersKey = (repo?: string, branch?: string) => ["SRC_FOLDERS", repo, branch];

  static getFeatureFlagsKey = (networkId: string) => ["FEATURE_FLAGS", networkId];

  static getPaymentMethodsKey = () => ["PAYMENT_METHODS"];

  static getWeeklyDeploymentCostKey = () => ["WEEKLY_DEPLOYMENT_COST"];

  static getLedgerRecordsKey = (address?: string) => (address ? ["LEDGER_RECORDS", address] : []);

  static getManagedWalletKey = (userId?: string) => ["MANAGED_WALLET", userId || ""];

  static getExportTransactionsCsvKey = (options: { startDate?: Date | null; endDate?: Date | null; timezone: string }) => {
    const key = ["EXPORT_TRANSACTIONS_CSV", options.timezone];

    if (options.startDate) {
      key.push("start_date", options.startDate.toISOString());
    }

    if (options.endDate) {
      key.push("end_date", options.endDate.toISOString());
    }

    return key;
  };
}
